import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, useGeneration } from "@/app";
import { TypedText, assistantRowVariants, userRowVariants, staggerContainer, chipItem, } from "./chatAnim";
import { createNewSong, createSongFromCustomText, editSongWithAI, editSongDirect, generateMusic, getSongById, waitForSongGeneration, searchReasons, searchGenres, searchMoods, searchTexts, } from "@/shared/api/songs";
import { transcribeVoice } from "@/shared/api/voice";
import { useAudioRecorder } from "@/features/voice/lib/useAudioRecorder";
import { MicLevelMeter } from "@/features/voice/ui/MicLevelMeter";
import { VOICE_MAX_DURATION_MS, appendTranscript, emptyTranscriptMessage, formatVoiceTimer, hasSpeechRecognitionApi, micDeniedMessage, micErrorMessage, resolveTranscribeErrorMessage, resolveVoiceInputStrategy, } from "@/shared/lib/voiceInput";
import { notifications, Loader } from "@/shared/ui";
import { IconMicrophone } from "@tabler/icons-react";
import { trackEvent } from "@/shared/lib/analytics";
import { AnalyticsEvent } from "@/shared/lib/analyticsEvents";
import { ymReachGoal, ymReachGoalOnce } from "@/shared/lib/yandexMetrika";
import { isGuestLlmLimitError, resolveTextActionErrorMessage, } from "@/shared/lib/guestLimitError";
import { setPendingMusicGeneration } from "@/shared/lib/pendingMusicGeneration";
import { isInsufficientBalanceError } from "@/shared/lib/insufficientBalanceError";
import { isTextNotFoundError } from "@/shared/lib/staleTextError";
import { markHasCreatedSongs } from "@/shared/lib/createdSongsFlag";
import { SONG_CREATION_STATE_STORAGE_KEY } from "@/shared/lib/songCreationPersistedState";
import { clearPendingSongGeneration, savePendingSongGeneration, } from "@/shared/lib/pendingSongGeneration";
import { CHAT_GENERATION_STATE_STORAGE_KEY } from "@/shared/lib/chatGenerationPersistedState";
import { isTelegramMiniApp } from "@/shared/lib/telegram";
import { isMaxMiniApp } from "@/shared/lib/max";
import { cn } from "@/shared/lib/utils";
import styles from "./ChatGenerateSongPage.module.scss";
const AVATAR_SRC = "/logo.jpg";
/** Текст после лирики — как в Telegram-боте: предложение доработать песню. */
const lyricsFollowUp = (ru) => ru
    ? "Как тебе такой вариант? Мы можем сделать песню ещё более личной, если ты расскажешь немного о вашем общем хобби или добавишь какое-то конкретное воспоминание. Также мы можем дописать красивый Бридж или эффектный Финал! 🤝\n\nХочешь — сделаю полноценную песню с музыкой и голосом?"
    : "How do you like this version? We can make the song even more personal if you share a hobby or a specific memory. We can also add a beautiful Bridge or a striking Finale! 🤝\n\nWant me to make a full song with music and voice?";
/** Полная история чата + выбор. Живёт, пока пользователь не завершит цикл. */
const CHAT_STATE_KEY = CHAT_GENERATION_STATE_STORAGE_KEY;
/** Опции голоса: значение для API (см. SongVoice m/f/d/c/h) + подписи ru/en + метка аналитики. */
const VOICE_OPTIONS = [
    { value: "m", ru: "👨 Мужской", en: "👨 Male", analytics: "male" },
    { value: "f", ru: "👩 Женский", en: "👩 Female", analytics: "female" },
    { value: "d", ru: "👫 Дуэт", en: "👫 Duet", analytics: "duet" },
    { value: "c", ru: "🧒 Детский", en: "🧒 Kids", analytics: "children" },
    { value: "h", ru: "🎼 Хор", en: "🎼 Choir", analytics: "choir" },
];
function readChatState() {
    try {
        const raw = localStorage.getItem(CHAT_STATE_KEY);
        if (!raw)
            return null;
        return JSON.parse(raw);
    }
    catch {
        return null;
    }
}
function writeChatState(s) {
    try {
        localStorage.setItem(CHAT_STATE_KEY, JSON.stringify(s));
    }
    catch {
        /* ignore */
    }
}
function clearChatState() {
    try {
        localStorage.removeItem(CHAT_STATE_KEY);
    }
    catch {
        /* ignore */
    }
}
export const ChatGenerateSongPage = ({ openRegisterModal, language, }) => {
    const ru = language === "ru";
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isGuest, isLoading, user, refreshUser, schedulePostEmailAuthAction, } = useAuth();
    const { setIsGeneratingText } = useGeneration();
    const catalogLanguage = isAuthenticated
        ? user?.language === "en"
            ? "en"
            : "ru"
        : language;
    // Каталоги
    const [reasons, setReasons] = useState([]);
    const [genres, setGenres] = useState([]);
    const [moods, setMoods] = useState([]);
    // Лента + шаг
    const [entries, setEntries] = useState([]);
    const [step, setStep] = useState("source");
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    // Голосовой ввод. Пути два (см. @/shared/lib/voiceInput):
    //  - Web Speech API, если он есть (десктопный Chrome) — мгновенно, без API;
    //  - иначе запись MediaRecorder + серверная расшифровка — так голос работает
    //    в webview Telegram/MAX и в iOS Safari, где SpeechRecognition нет.
    // speechSupported определяем на маунте (window доступен только в браузере).
    const [speechSupported, setSpeechSupported] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const recognitionRef = useRef(null);
    const voiceBaseRef = useRef("");
    const recorder = useAudioRecorder({ maxDurationMs: VOICE_MAX_DURATION_MS });
    const [isTranscribing, setIsTranscribing] = useState(false);
    // Blob последней отправленной записи — гарантирует ровно одну отправку на
    // запись (эффект ниже перезапускается на любом ререндере страницы).
    const sentBlobRef = useRef(null);
    // Гасит setState после ухода со страницы, пока летит запрос расшифровки.
    const voiceUnmountedRef = useRef(false);
    const voiceStrategy = resolveVoiceInputStrategy({
        hasSpeechRecognition: speechSupported,
        canRecord: recorder.isSupported,
    });
    // Выбор
    const [source, setSource] = useState("ai");
    const [reasonTitle, setReasonTitle] = useState("");
    const [styleHint, setStyleHint] = useState("");
    const [about, setAbout] = useState("");
    const [createdSong, setCreatedSong] = useState(null);
    const [voice, setVoice] = useState("m");
    const [genreTitle, setGenreTitle] = useState(null);
    const [selMoods, setSelMoods] = useState([]);
    // Сохранённые
    const [savedTexts, setSavedTexts] = useState([]);
    const [savedQuery, setSavedQuery] = useState("");
    const idRef = useRef(0);
    const ledgerRef = useRef(null);
    const threadRef = useRef(null);
    // Кол-во сообщений — чтобы НЕ автоскроллить на стартовом экране (hero с
    // карточками поводов): там прокрутка вниз спрятала бы «предложенные» вверху.
    const entriesLenRef = useRef(0);
    const bootedRef = useRef(false);
    const persistedRef = useRef(readChatState());
    // id сообщений, добавленных в этой сессии (их анимируем/печатаем). У
    // восстановленных из storage сообщений id сюда не попадают — они появляются
    // мгновенно, без повторной анимации.
    const animatedIdsRef = useRef(new Set());
    const nextId = () => `e${++idRef.current}`;
    // Реальный скроллер ленты — НЕ всегда сама лента: страница чата целиком выходит
    // за скролл-рут мини-аппа (из-за спейсера под таб-бар), и тогда прокручивается
    // именно внешний скролл-рут, а не .ledger (её scrollTop был бессмысленным —
    // из-за этого автоскролл «не работал»). Скроллим обоих: непереполненный — no-op.
    const scrollToBottom = () => {
        const apply = () => {
            const ledger = ledgerRef.current;
            if (!ledger)
                return;
            ledger.scrollTop = ledger.scrollHeight;
            const root = ledger.closest("[data-scroll-root]");
            if (root)
                root.scrollTop = root.scrollHeight;
        };
        apply();
        // iOS/мобилка: высота ответа бота (печатающийся текст, аватар, список)
        // часто досчитывается на следующий кадр — scrollTop сразу не «липнет».
        // Повторяем в rAF, чтобы автопрокрутка реально доходила до низа.
        if (typeof requestAnimationFrame !== "undefined")
            requestAnimationFrame(apply);
    };
    /** Прижаты ли к низу того скроллера, что реально переполнен. */
    const isNearBottom = () => {
        const ledger = ledgerRef.current;
        if (!ledger)
            return true;
        const near = (el) => el.scrollHeight - el.scrollTop - el.clientHeight < 160;
        if (ledger.scrollHeight > ledger.clientHeight + 2)
            return near(ledger);
        const root = ledger.closest("[data-scroll-root]");
        if (root && root.scrollHeight > root.clientHeight + 2)
            return near(root);
        return true;
    };
    const pushAssistant = (e) => setEntries((prev) => {
        const id = nextId();
        animatedIdsRef.current.add(id);
        return [...prev, { id, role: "assistant", ...e }];
    });
    const pushUser = (text) => setEntries((prev) => {
        const id = nextId();
        animatedIdsRef.current.add(id);
        return [...prev, { id, role: "user", text }];
    });
    // Автоскролл вниз при изменении ленты/шага — только когда есть диалог.
    // На стартовом экране (hero) не скроллим, иначе карточки поводов уезжают вверх.
    useEffect(() => {
        entriesLenRef.current = entries.length;
        if (entries.length > 0)
            scrollToBottom();
    }, [entries, step, busy, progress]);
    // Держим ленту прижатой к низу, пока контент РАСТЁТ уже после коммита:
    // печатающийся ответ (TypedText), список вопросов, анимация появления строки,
    // подгрузка аватара. Одноразовый scrollToBottom в useEffect выше срабатывает
    // до того, как высота досчитается, — поэтому дополнительно наблюдаем высоту
    // содержимого. Если пользователь сам отмотал вверх (читает историю) — не
    // дёргаем его: скроллим только когда он у низа.
    useEffect(() => {
        const content = threadRef.current;
        if (!content)
            return;
        const ro = new ResizeObserver(() => {
            if (entriesLenRef.current > 0 && isNearBottom())
                scrollToBottom();
        });
        ro.observe(content);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Аналитика + загрузка каталогов
    useEffect(() => {
        trackEvent(AnalyticsEvent.TEXT_GEN_SCREEN_OPEN, { entry_point: "chat" });
        trackEvent(AnalyticsEvent.TEXT_AI_VIEW);
        ymReachGoalOnce("text_gen_screen_open", { mode: "chat" });
        let cancelled = false;
        void (async () => {
            try {
                const [r, g, m] = await Promise.all([
                    searchReasons({ limit: 100, language: catalogLanguage }),
                    searchGenres({ limit: 100, language: catalogLanguage }),
                    searchMoods({ limit: 100, language: catalogLanguage }),
                ]);
                if (cancelled)
                    return;
                setReasons(r);
                setGenres(g);
                setMoods(m);
            }
            catch {
                /* каталоги не критичны для рендера hero */
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogLanguage]);
    // Сохраняем всю историю чата + выбор, пока цикл не завершён (после успешной
    // генерации песни состояние очищаем). Не пишем до восстановления (boot),
    // иначе перетрём сохранённое пустым стартовым состоянием.
    useEffect(() => {
        if (!bootedRef.current)
            return;
        writeChatState({
            entries,
            step,
            source,
            reasonTitle,
            styleHint,
            genreTitle,
            about,
            createdSong,
            voice,
            moods: selMoods,
        });
    }, [
        entries,
        step,
        source,
        reasonTitle,
        styleHint,
        genreTitle,
        about,
        createdSong,
        voice,
        selMoods,
    ]);
    // Зеркалим id текста в общий ключ черновика — он нужен восстановлению ввода.
    // Решение «резюмить ли генерацию после оплаты» на нём НЕ строится: для этого
    // есть маркер намерения, который ставится по 402 (см. pendingSongGeneration).
    useEffect(() => {
        if (!createdSong)
            return;
        const textId = Number(createdSong.text_id ?? createdSong.id);
        if (!Number.isFinite(textId) || textId <= 0)
            return;
        try {
            const prev = localStorage.getItem(SONG_CREATION_STATE_STORAGE_KEY);
            const base = prev ? JSON.parse(prev) : {};
            localStorage.setItem(SONG_CREATION_STATE_STORAGE_KEY, JSON.stringify({ ...base, pendingMelodySongId: textId }));
        }
        catch {
            /* ignore */
        }
    }, [createdSong]);
    // Прогресс-анимация во время ожидания генерации.
    const runProgress = () => {
        setProgress(8);
        const t = setInterval(() => {
            setProgress((p) => (p < 92 ? p + Math.max(1, Math.round((95 - p) / 8)) : p));
        }, 600);
        return () => clearInterval(t);
    };
    const notifyError = (e, fallbackRu, fallbackEn) => {
        notifications.show({
            title: ru ? "Ошибка" : "Error",
            message: resolveTextActionErrorMessage(e, language, fallbackRu, fallbackEn),
            color: "red",
        });
    };
    // ---- ТЕКСТ ----
    const askStyle = () => {
        pushAssistant({
            body: ru
                ? "Отличный выбор! Какой стиль текста вам ближе? Или просто напишите, что чувствуете."
                : "Great choice! Which lyric style do you prefer? Or just type how you feel.",
        });
        setStep("style");
    };
    const askAbout = () => {
        pushAssistant({
            title: ru ? "Теперь самое важное!" : "Now the most important part!",
            body: ru
                ? "Стиль и повод выбраны — расскажите про человека, и я напишу для него настоящий текст 🎯"
                : "Style and occasion are set — tell me about the person and I'll write real lyrics 🎯",
            list: ru
                ? [
                    "– Для кого песня?",
                    "– Сколько лет (если день рождения)?",
                    "– Что делает его особенным?",
                    "– Истории, шутки или любимые фразы?",
                    "– Какие чувства передать?",
                ]
                : [
                    "– Who is the song for?",
                    "– Age (if a birthday)?",
                    "– What makes them special?",
                    "– Stories, jokes or favourite phrases?",
                    "– Which feelings to convey?",
                ],
        });
        setStep("about");
    };
    const pickReason = (title) => {
        setSource("ai");
        setReasonTitle(title);
        pushUser(title);
        trackEvent(AnalyticsEvent.REASON_SELECT, { value: title });
        askStyle();
    };
    const pickStyle = (g) => {
        setGenreTitle(g.title);
        setStyleHint(g.title);
        pushUser(g.title);
        trackEvent(AnalyticsEvent.GENRE_SELECT, { value: g.title });
        askAbout();
    };
    const startTextGen = async (aboutText) => {
        setSource("ai");
        setAbout(aboutText);
        setStep("genText");
        setIsGeneratingText(true);
        setBusy(true);
        const stop = runProgress();
        try {
            trackEvent(AnalyticsEvent.TEXT_GEN_START, {
                occasion: reasonTitle || null,
                recipient: "—",
                about_len: aboutText.length,
                language: catalogLanguage,
            });
            ymReachGoalOnce("text_gen_start", { mode: "chat" });
            const { ws_event_id } = await createNewSong({
                about: aboutText,
                language: catalogLanguage,
                recipient: "—",
                reason: reasonTitle || "",
            });
            const song = await waitForSongGeneration(ws_event_id);
            setProgress(100);
            setCreatedSong(song);
            pushAssistant({
                result: { title: song.title || "", lyrics: song.text || "" },
                body: lyricsFollowUp(ru),
            });
            setStep("textResult");
        }
        catch (e) {
            if (isGuestLlmLimitError(e)) {
                notifyError(e, "Ошибка при генерации текста", "Error generating text");
            }
            else {
                notifyError(e, "Ошибка при генерации текста", "Error generating text");
            }
            pushAssistant({
                body: ru
                    ? "Не получилось сгенерировать текст. Попробуйте ещё раз."
                    : "Couldn't generate the text. Please try again.",
            });
            setStep("about");
        }
        finally {
            stop();
            setBusy(false);
            setIsGeneratingText(false);
        }
    };
    const regenerateText = async () => {
        if (busy || !about)
            return;
        pushUser(ru ? "Перегенерировать" : "Regenerate");
        await startTextGen(about);
    };
    const askForEdits = () => {
        if (busy)
            return;
        pushAssistant({
            body: ru
                ? "Скажи, что хочешь изменить в песне, и я внесу правки 😉"
                : "Tell me what you want to change in the song, and I'll edit it 😉",
        });
    };
    const startCustomText = async (text) => {
        setSource("custom");
        setStep("genText");
        setBusy(true);
        const stop = runProgress();
        try {
            trackEvent(AnalyticsEvent.TEXT_MANUAL_INPUT, {
                text_len: text.length,
                language: catalogLanguage,
            });
            const { ws_event_id } = await createSongFromCustomText({
                text,
                recipient: null,
                reason: null,
            });
            const song = await waitForSongGeneration(ws_event_id);
            setProgress(100);
            setCreatedSong(song);
            pushAssistant({
                result: { title: song.title || "", lyrics: song.text || text },
                body: lyricsFollowUp(ru),
            });
            setStep("textResult");
        }
        catch (e) {
            notifyError(e, "Ошибка при создании песни", "Error creating song");
            setStep("customInput");
        }
        finally {
            stop();
            setBusy(false);
        }
    };
    const pickSavedText = (t) => {
        setSource("saved");
        trackEvent(AnalyticsEvent.TEXT_SELECT, { text_id: t.id, source: "history" });
        const song = {
            id: t.id,
            title: t.title || "",
            text: t.text,
            status: "",
            text_id: t.id,
            audios: [],
            created_at: t.created_at,
        };
        setCreatedSong(song);
        pushUser(t.title || (ru ? "Выбранный текст" : "Selected text"));
        pushAssistant({
            result: { title: song.title, lyrics: song.text },
            body: lyricsFollowUp(ru),
        });
        setStep("textResult");
    };
    const editTextWithAi = async (prompt) => {
        if (!createdSong)
            return;
        const textId = createdSong.text_id || createdSong.id;
        pushUser(prompt);
        setStep("genText");
        setBusy(true);
        const stop = runProgress();
        try {
            trackEvent(AnalyticsEvent.TEXT_EDIT_START, { text_id: textId });
            const { ws_event_id } = await editSongWithAI(textId, { prompt });
            const song = await waitForSongGeneration(ws_event_id);
            setProgress(100);
            setCreatedSong(song);
            pushAssistant({
                result: { title: song.title || "", lyrics: song.text || "" },
                body: lyricsFollowUp(ru),
            });
            setStep("textResult");
        }
        catch (e) {
            notifyError(e, "Ошибка при редактировании текста", "Error editing text");
            setStep("textResult");
        }
        finally {
            stop();
            setBusy(false);
        }
    };
    // ---- МУЗЫКА ----
    const goToMusic = async () => {
        if (busy)
            return;
        // Фиксируем текст перед музыкой через /texts/{id}/edit-direct — как в
        // классическом флоу («Продолжить с этим текстом»): бэк возвращает
        // канонический text_id, который и нужен generate-music.
        if (createdSong) {
            const textId = createdSong.text_id || createdSong.id;
            if (textId) {
                setBusy(true);
                try {
                    const committed = await editSongDirect(textId, {
                        text: createdSong.text || "",
                        title: createdSong.title?.trim() || undefined,
                    });
                    setCreatedSong({
                        ...createdSong,
                        ...committed,
                        text_id: committed.text_id ??
                            committed.id ??
                            createdSong.text_id ??
                            createdSong.id,
                        id: committed.id ?? createdSong.id,
                    });
                }
                catch (e) {
                    // Не блокируем переход — попробуем сгенерировать музыку с тем, что есть.
                    console.error("edit-direct before music failed:", e);
                }
                finally {
                    setBusy(false);
                }
            }
        }
        pushAssistant({
            title: ru ? "Перейдём к музыке 🎵" : "Let's move to music 🎵",
            body: ru ? "Какой голос подойдёт?" : "Which voice fits best?",
        });
        setStep("voice");
    };
    const pickVoice = (v) => {
        setVoice(v);
        const opt = VOICE_OPTIONS.find((o) => o.value === v) ?? VOICE_OPTIONS[0];
        pushUser(ru ? opt.ru : opt.en);
        trackEvent(AnalyticsEvent.VOICE_SELECT, {
            value: opt.analytics,
            text_id: createdSong?.text_id ?? createdSong?.id,
        });
        // Жанр уже выбран на шаге «стиль» — спрашиваем настроение.
        pushAssistant({
            body: ru
                ? "Какое настроение? Можно выбрать несколько."
                : "What mood? You can pick several.",
        });
        setStep("moods");
    };
    const advanceFromMoods = () => {
        if (selMoods.length)
            pushUser(selMoods.map((m) => m.title).join(", "));
        pushAssistant({
            title: ru ? "Готово к созданию!" : "Ready to create!",
            body: ru
                ? "Нажмите «Создать песню» — соберу музыку по вашим настройкам."
                : "Tap «Create song» — I'll assemble the music.",
        });
        setStep("confirm");
    };
    // Низкоуровневый запуск генерации музыки с явными настройками — чтобы можно
    // было вызвать и из текущего состояния, и из восстановленного после оплаты.
    const runMusic = async (song, sel) => {
        const textId = song.text_id || song.id;
        // Без валидного text_id запрос упадёт 422 — лучше понятная ошибка.
        if (!textId || !Number.isFinite(Number(textId))) {
            notifications.show({
                title: ru ? "Ошибка" : "Error",
                message: ru
                    ? "Не удалось определить текст песни. Сгенерируйте текст заново."
                    : "Could not resolve the song text. Please regenerate it.",
                color: "red",
            });
            setStep("textResult");
            return;
        }
        setStep("genMusic");
        setBusy(true);
        const stop = runProgress();
        try {
            trackEvent(AnalyticsEvent.SONG_GEN_START, {
                text_id: textId,
                genre: sel.genreTitle,
                voice: sel.voice,
                moods_count: sel.moods.length,
            });
            // Та же ступень в Метрике (отдельная от собственной аналитики система,
            // см. yandexMetrika): «пользователь реально запустил песню».
            ymReachGoal("song_gen_start", { mode: "chat" });
            const { ws_event_id } = await generateMusic({
                text_id: textId,
                voice: sel.voice,
                genre: sel.genreTitle,
                moods: sel.moods.length ? sel.moods : null,
                instruments: null,
                key: null,
                extra_description: null,
            });
            await refreshUser();
            setPendingMusicGeneration({ ws_event_id, song_id: song.id });
            // Песня создана → показываем вкладку «Мои песни» в сайдбаре (в т.ч. гостю).
            markHasCreatedSongs();
            try {
                localStorage.removeItem(SONG_CREATION_STATE_STORAGE_KEY);
            }
            catch {
                /* ignore */
            }
            // Генерация запущена — намерение исполнено, маркер больше не нужен.
            clearPendingSongGeneration();
            // Цикл создания песни завершён — очищаем сохранённую историю чата.
            clearChatState();
            const isMiniApp = isTelegramMiniApp() || isMaxMiniApp();
            notifications.show({
                title: ru ? "Генерация начата" : "Generation started",
                message: isMiniApp
                    ? ru
                        ? "Мы начали генерировать песню — бот пришлёт уведомление, когда будет готово"
                        : "We started generating your song — the bot will notify you when ready"
                    : ru
                        ? "Открываем «Мои песни» — трек появится, когда музыка будет готова"
                        : "Opening My Songs — your track will appear when ready",
                color: "blue",
                autoClose: 5000,
            });
            navigate("/my-songs", {
                replace: true,
                state: {
                    pendingMusicWsEventId: ws_event_id,
                    pendingMusicSongId: song.id,
                },
            });
        }
        catch (e) {
            // Нехватка баланса (402) — 401-интерсептор уже открыл попап пополнения.
            // Не показываем красную ошибку с сырым detail «Insufficient SONGS balance».
            if (isInsufficientBalanceError(e)) {
                // Пользователь нажал «Создать песню» и упёрся в пейвол — только теперь
                // рождается намерение «оплачиваю генерацию». Возврат с оплаты резюмит
                // генерацию исключительно по этому маркеру (см. pendingSongGeneration).
                savePendingSongGeneration(Number(textId));
                setStep("confirm");
                return;
            }
            // Черновик устарел: text_id из прошлой сессии/аккаунта
            // («Text(id=…) not found»). Вместо красной ошибки — тихо сбрасываем
            // черновик и начинаем создание заново.
            if (isTextNotFoundError(e)) {
                clearChatState();
                setCreatedSong(null);
                setSource("ai");
                setReasonTitle("");
                setStyleHint("");
                setGenreTitle(null);
                setAbout("");
                setSelMoods([]);
                pushAssistant({
                    title: ru ? "Черновик устарел" : "Draft expired",
                    body: ru
                        ? "Текст из прошлой сессии больше недоступен. Давайте создадим песню заново — о чём она будет?"
                        : "The text from a previous session is no longer available. Let's start over — what's the song about?",
                });
                setStep("source");
                return;
            }
            // Диагностика 422: показываем поле(я) из detail в консоли.
            console.error("generate-music failed. text_id:", textId, "selection:", sel, "detail:", e?.response?.data
                ?.detail);
            notifyError(e, "Ошибка при генерации музыки", "Error generating music");
            setStep("confirm");
        }
        finally {
            stop();
            setBusy(false);
        }
    };
    const currentMusicSelection = () => ({
        voice,
        genreTitle,
        moods: selMoods.map((m) => m.title),
    });
    const doGenerateMusic = async () => {
        if (!createdSong)
            return;
        await runMusic(createdSong, currentMusicSelection());
    };
    const handleCreateSong = () => {
        if (!createdSong || busy)
            return;
        // Гостевой чекаут: гость генерирует музыку без регистрации (лимиты/баланс
        // проверяет бэк). Регистрацию форсируем только полностью анонимному.
        if (!isAuthenticated && !isGuest) {
            schedulePostEmailAuthAction(() => {
                void doGenerateMusic();
            });
            openRegisterModal();
            return;
        }
        void doGenerateMusic();
    };
    // ---- Возврат к выбору текста / новая генерация ----
    const resetToSource = () => {
        setSource("ai");
        setReasonTitle("");
        setStyleHint("");
        setGenreTitle(null);
        setAbout("");
        setCreatedSong(null);
        setSelMoods([]);
        pushAssistant({
            body: ru
                ? "Хорошо, начнём текст заново. О чём будет песня?"
                : "Okay, let's start the text again. What's it about?",
        });
        setStep("source");
    };
    const startOver = () => {
        if (busy)
            return;
        pushUser(ru ? "Начать заново" : "Start over");
        resetToSource();
    };
    const backToText = () => {
        if (createdSong) {
            setStep("textResult");
        }
        else {
            resetToSource();
        }
    };
    // ---- Восстановление состояния / возврат после оплаты ----
    const applyPersisted = (p) => {
        setEntries(Array.isArray(p.entries) ? p.entries : []);
        idRef.current = (p.entries?.length || 0) + 1;
        setSource(p.source || "ai");
        setReasonTitle(p.reasonTitle || "");
        setStyleHint(p.styleHint || "");
        setGenreTitle(p.genreTitle ?? null);
        setAbout(p.about || "");
        setCreatedSong(p.createdSong ?? null);
        setVoice(p.voice || "m");
        setSelMoods(Array.isArray(p.moods) ? p.moods : []);
        let s = p.step;
        // Шаги с незавершённым async после перезагрузки не возобновляем.
        if (s === "genText") {
            s = p.createdSong ? "textResult" : p.source === "custom" ? "customInput" : "about";
        }
        else if (s === "genMusic") {
            s = "confirm";
        }
        else if (s === "instruments" || s === "key" || s === "extra") {
            // Удалённые шаги настроек — сразу к подтверждению.
            s = "confirm";
        }
        setStep(s);
    };
    useEffect(() => {
        if (isLoading || bootedRef.current)
            return;
        bootedRef.current = true;
        const navState = location.state;
        const p = persistedRef.current;
        // Возврат после оплаты: восстановить выбор и СРАЗУ запустить генерацию
        // (баланс уже пополнен — списываем и генерируем без повторного клика).
        // Гость тоже: гостевой чекаут.
        if (navState?.resumeSongSettings && (isAuthenticated || isGuest) && p?.createdSong) {
            applyPersisted(p);
            navigate(`${location.pathname}`, { replace: true, state: {} });
            void (async () => {
                let song = p.createdSong;
                const id = Number(song.text_id ?? song.id);
                if (Number.isFinite(id) && id > 0) {
                    try {
                        song = await getSongById(id);
                        setCreatedSong(song);
                    }
                    catch {
                        /* используем сохранённый объект */
                    }
                }
                pushAssistant({
                    title: ru ? "С возвращением!" : "Welcome back!",
                    body: ru
                        ? "Оплата прошла — запускаю генерацию песни."
                        : "Payment is done — starting the song generation.",
                });
                await runMusic(song, {
                    voice: p.voice,
                    genreTitle: p.genreTitle,
                    moods: (p.moods || []).map((m) => m.title),
                });
            })();
            return;
        }
        // Обычное восстановление истории чата (цикл не завершён).
        if (p && Array.isArray(p.entries) && p.entries.length) {
            applyPersisted(p);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);
    // ---- Ввод снизу ----
    const inputConfig = () => {
        switch (step) {
            case "source":
                return {
                    enabled: true,
                    placeholder: ru ? "Опиши свою песню…" : "Describe your song…",
                };
            case "style":
                return {
                    enabled: true,
                    placeholder: ru ? "Или напишите, что чувствуете…" : "Or type how you feel…",
                };
            case "about":
                return {
                    enabled: true,
                    placeholder: ru ? "Опиши, для кого песня…" : "Describe who it's for…",
                };
            case "customInput":
                return {
                    enabled: true,
                    placeholder: ru ? "Вставьте свой текст…" : "Paste your text…",
                };
            case "savedPick":
                return {
                    enabled: true,
                    placeholder: ru ? "Поиск по текстам…" : "Search texts…",
                };
            case "textResult":
                return {
                    enabled: true,
                    placeholder: ru
                        ? "Напиши, что изменить в тексте…"
                        : "Type what to change in the lyrics…",
                };
            default:
                return {
                    enabled: false,
                    placeholder: ru ? "Выберите вариант выше" : "Pick an option above",
                };
        }
    };
    // Feature-detect Web Speech API один раз на маунте + аборт распознавания при
    // размонтировании, чтобы «висящая» сессия не досылала результаты.
    useEffect(() => {
        setSpeechSupported(hasSpeechRecognitionApi());
        voiceUnmountedRef.current = false;
        return () => {
            voiceUnmountedRef.current = true;
            recognitionRef.current?.abort();
        };
    }, []);
    const startSpeechRecognition = () => {
        const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!Ctor)
            return;
        if (isRecording) {
            recognitionRef.current?.stop();
            return;
        }
        const recognition = new Ctor();
        recognition.lang = "ru-RU";
        recognition.interimResults = true;
        recognition.continuous = false;
        // Дописываем к уже введённому тексту, а не затираем его.
        voiceBaseRef.current = input.trim() ? input.trim() + " " : "";
        recognition.onresult = (event) => {
            let transcript = "";
            for (let i = 0; i < event.results.length; i++) {
                transcript += event.results[i][0].transcript;
            }
            // НЕ отправляем автоматически — только подставляем в поле ввода.
            setInput(voiceBaseRef.current + transcript);
        };
        recognition.onerror = () => setIsRecording(false);
        recognition.onend = () => {
            setIsRecording(false);
            recognitionRef.current = null;
        };
        recognitionRef.current = recognition;
        setIsRecording(true);
        recognition.start();
    };
    // Идёт запись/расшифровка — этим состояниям кнопка микрофона подсвечивается
    // и, самое главное, остаётся кликабельной (иначе запись нечем остановить).
    const isMicRecording = isRecording || recorder.state === "recording";
    // Одна кнопка — две стратегии: Web Speech API там, где он есть; иначе запись
    // с отправкой на серверную расшифровку.
    const onMicClick = () => {
        if (voiceStrategy === "speech") {
            startSpeechRecognition();
            return;
        }
        if (voiceStrategy !== "record" || isTranscribing)
            return;
        if (recorder.state === "recording") {
            recorder.stop();
            return;
        }
        void recorder.start();
    };
    // Запись остановлена (вручную или авто-стопом на VOICE_MAX_DURATION_MS) —
    // отправляем на расшифровку. Распознанное ДОПИСЫВАЕМ к уже введённому и
    // НЕ отправляем автоматически — ровно как на пути Web Speech API.
    useEffect(() => {
        if (recorder.state !== "recorded" || !recorder.blob)
            return;
        if (sentBlobRef.current === recorder.blob)
            return;
        sentBlobRef.current = recorder.blob;
        const file = recorder.toFile("chat-voice");
        // Случайный тап «запись → сразу стоп» даёт пустой файл: бэк ответил бы 400,
        // поэтому такую запись даже не отправляем — сообщение то же, что и на тишину.
        if (!file || file.size === 0) {
            notifications.show({
                title: ru ? "Тишина" : "Silence",
                message: emptyTranscriptMessage(language),
                color: "orange",
            });
            recorder.reset();
            return;
        }
        setIsTranscribing(true);
        void (async () => {
            try {
                const text = await transcribeVoice(file);
                if (voiceUnmountedRef.current)
                    return;
                if (!text.trim()) {
                    // Тишина в записи — это не ошибка ручки, но и молча подставлять
                    // пустоту нельзя: человек решит, что кнопка сломана.
                    notifications.show({
                        title: ru ? "Тишина" : "Silence",
                        message: emptyTranscriptMessage(language),
                        color: "orange",
                    });
                    return;
                }
                setInput((prev) => appendTranscript(prev, text));
            }
            catch (e) {
                if (voiceUnmountedRef.current)
                    return;
                notifications.show({
                    title: ru ? "Ошибка" : "Error",
                    message: resolveTranscribeErrorMessage(e, language),
                    color: "red",
                });
            }
            finally {
                if (!voiceUnmountedRef.current) {
                    setIsTranscribing(false);
                    // reset() снимает и blob, и previewUrl — держать ссылку на запись
                    // (это мегабайты) после отправки незачем.
                    sentBlobRef.current = null;
                    recorder.reset();
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recorder.state, recorder.blob, language]);
    // Подсказка над строкой ввода: держится, пока держится состояние (в отличие
    // от тоста), — поэтому отказ в доступе к микрофону виден, сколько нужно.
    const voiceHint = voiceStrategy !== "record"
        ? null
        : recorder.state === "denied"
            ? micDeniedMessage(language)
            : recorder.state === "error"
                ? micErrorMessage(language)
                : isTranscribing
                    ? ru
                        ? "Расшифровываю…"
                        : "Transcribing…"
                    : null;
    const onSend = () => {
        const value = input.trim();
        if (step === "savedPick") {
            setSavedQuery(value);
            void loadSaved(value);
            return;
        }
        if (!value || busy)
            return;
        setInput("");
        switch (step) {
            case "source":
                pickReasonFree(value);
                break;
            case "style":
                setStyleHint(value);
                pushUser(value);
                askAbout();
                break;
            case "about":
                pushUser(value);
                void startTextGen(value);
                break;
            case "customInput":
                pushUser(value);
                void startCustomText(value);
                break;
            case "textResult":
                void editTextWithAi(value);
                break;
            default:
                break;
        }
    };
    const pickReasonFree = (title) => {
        setSource("ai");
        setReasonTitle(title);
        pushUser(title);
        askStyle();
    };
    const loadSaved = async (q) => {
        setBusy(true);
        try {
            const res = await searchTexts({ search: q || null, limit: 100, offset: 0 });
            setSavedTexts(res.results);
            const trimmed = q.trim();
            if (trimmed) {
                trackEvent(AnalyticsEvent.TEXT_SEARCH, {
                    query_len: trimmed.length,
                    results_count: res.results.length,
                });
            }
        }
        catch (e) {
            notifyError(e, "Не удалось загрузить тексты", "Failed to load texts");
        }
        finally {
            setBusy(false);
        }
    };
    const openCustom = () => {
        trackEvent(AnalyticsEvent.TEXT_MANUAL_VIEW);
        setSource("custom");
        pushAssistant({
            title: ru ? "Свой текст" : "Your text",
            body: ru
                ? "Вставьте готовый текст песни — я подготовлю его к музыке."
                : "Paste your ready lyrics — I'll prepare them for music.",
        });
        setStep("customInput");
    };
    const openSaved = () => {
        trackEvent(AnalyticsEvent.TEXT_HISTORY_VIEW);
        setSource("saved");
        pushAssistant({
            title: ru ? "Сохранённые тексты" : "Saved texts",
            body: ru ? "Выберите текст из сохранённых." : "Pick one of your saved texts.",
        });
        setStep("savedPick");
        void loadSaved("");
    };
    // ---- Рендер текущего контрола ----
    const renderControl = () => {
        if (busy && (step === "genText" || step === "genMusic")) {
            return (_jsxs(motion.div, { className: styles.row, initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.28, ease: "easeOut" }, children: [_jsx("img", { className: styles.avatar, src: AVATAR_SRC, alt: "" }), _jsxs("div", { className: styles.assistantBubble, children: [_jsxs("div", { className: styles.progressTitle, children: [_jsx("span", { className: styles.progressDot }), step === "genMusic"
                                        ? ru
                                            ? "Виртуоз сочиняет музыку"
                                            : "Virtuoz is composing"
                                        : ru
                                            ? "Виртуоз пишет текст"
                                            : "Virtuoz is writing", " ", "\u00B7 ", progress, "%"] }), _jsx("div", { className: styles.progressTrack, children: _jsx("div", { className: styles.progressFill, style: { width: `${progress}%` } }) }), _jsx("div", { className: styles.progressHint, children: ru
                                    ? "Обычно занимает около минуты. Не закрывайте страницу."
                                    : "Usually takes about a minute. Keep the page open." })] })] }));
        }
        if (step === "source") {
            // На старте повод показывает hero (entries пуст). После начала диалога
            // (возврат к выбору текста) рендерим повод как контрол.
            if (entries.length === 0)
                return null;
            return (_jsxs("div", { children: [_jsx(motion.div, { className: styles.chipRow, variants: staggerContainer, initial: "hidden", animate: "show", children: reasons.map((r) => (_jsx(motion.button, { className: styles.optionCardChip, onClick: () => pickReason(r.title), variants: chipItem, children: r.title }, r.id))) }), _jsxs("div", { className: styles.secondaryRow, children: [_jsx("button", { className: styles.secondaryPill, onClick: openCustom, children: ru ? "Вставить свой текст" : "Paste your text" }), _jsx("button", { className: styles.secondaryPill, onClick: openSaved, children: ru ? "Мои сохранённые" : "My saved" })] })] }));
        }
        if (step === "style") {
            return (_jsx(motion.div, { className: styles.chipRow, variants: staggerContainer, initial: "hidden", animate: "show", children: genres.map((g) => (_jsx(motion.button, { className: styles.optionCardChip, onClick: () => pickStyle(g), variants: chipItem, children: g.title }, g.id))) }));
        }
        if (step === "voice") {
            return (_jsxs("div", { children: [_jsx(motion.div, { className: styles.chipRow, variants: staggerContainer, initial: "hidden", animate: "show", children: VOICE_OPTIONS.map((o) => (_jsx(motion.button, { className: styles.optionCardChip, onClick: () => pickVoice(o.value), variants: chipItem, children: ru ? o.ru : o.en }, o.value))) }), _jsx("div", { className: styles.secondaryRow, children: _jsxs("button", { className: styles.secondaryPill, onClick: backToText, children: ["\u2190 ", ru ? "К тексту" : "Back to text"] }) })] }));
        }
        if (step === "moods") {
            return (_jsxs("div", { children: [_jsx(motion.div, { className: styles.chipRow, variants: staggerContainer, initial: "hidden", animate: "show", children: moods.map((m) => {
                            const sel = selMoods.some((x) => x.id === m.id);
                            return (_jsx(motion.button, { className: cn(styles.optionCardChip, sel && styles.chipSelected), variants: chipItem, onClick: () => {
                                    if (sel)
                                        setSelMoods(selMoods.filter((x) => x.id !== m.id));
                                    else {
                                        setSelMoods([...selMoods, m]);
                                        trackEvent(AnalyticsEvent.MOOD_SELECT, {
                                            value: m.title,
                                            text_id: createdSong?.text_id ?? createdSong?.id,
                                        });
                                    }
                                }, children: m.title }, m.id));
                        }) }), _jsx("div", { className: styles.secondaryRow, children: _jsx("button", { className: styles.primaryPill, onClick: advanceFromMoods, children: selMoods.length ? (ru ? "Продолжить" : "Continue") : ru ? "Пропустить" : "Skip" }) })] }));
        }
        if (step === "savedPick") {
            return (_jsx("div", { className: styles.savedList, children: savedTexts.length === 0 ? (_jsx("div", { className: styles.emptyHint, children: savedQuery
                        ? ru
                            ? "Ничего не найдено"
                            : "Nothing found"
                        : ru
                            ? "Сохранённых текстов пока нет"
                            : "No saved texts yet" })) : (savedTexts.map((t) => (_jsxs("button", { className: styles.savedCard, onClick: () => pickSavedText(t), children: [_jsx("div", { className: styles.savedCardTitle, children: t.title || (ru ? "Без названия" : "Untitled") }), _jsx("div", { className: styles.savedCardPreview, children: t.text })] }, t.id)))) }));
        }
        if (step === "textResult") {
            return (_jsxs("div", { className: styles.resultActions, children: [_jsxs("div", { className: styles.secondaryRow, children: [_jsxs("button", { className: styles.secondaryPill, onClick: askForEdits, disabled: busy, children: ["\u270F ", ru ? "Внести правки" : "Edit lyrics"] }), source === "ai" && (_jsxs("button", { className: styles.secondaryPill, onClick: () => void regenerateText(), disabled: busy, children: ["\u21BB ", ru ? "Перегенерировать" : "Regenerate"] })), _jsxs("button", { className: styles.secondaryPill, onClick: startOver, disabled: busy, children: ["\u270E ", ru ? "Начать заново" : "Start over"] })] }), _jsxs("button", { className: styles.primaryPillWide, onClick: () => void goToMusic(), disabled: busy, children: ["\u2192 ", ru ? "Перейти к музыке" : "Go to music"] })] }));
        }
        if (step === "confirm") {
            return (_jsxs("div", { className: styles.resultActions, children: [_jsx("button", { className: styles.primaryPillWide, onClick: handleCreateSong, disabled: busy, children: ru ? "Создать песню" : "Create song" }), _jsxs("button", { className: styles.secondaryPill, onClick: backToText, disabled: busy, children: ["\u2190 ", ru ? "К тексту" : "Back to text"] })] }));
        }
        return null;
    };
    const cfg = inputConfig();
    return (_jsxs("div", { className: styles.page, children: [_jsxs("div", { className: styles.header, children: [_jsx("img", { className: styles.headerAvatar, src: AVATAR_SRC, alt: "" }), _jsxs("div", { className: styles.headerText, children: [_jsx("span", { className: styles.headerTitle, children: ru ? "ВИРТУОЗ" : "VIRTUOZ" }), _jsx("span", { className: styles.headerSub, children: ru ? "2 млн+ создано песен" : "2M+ songs created" })] }), _jsx("div", { className: styles.headerSpacer })] }), _jsx("div", { className: styles.ledger, ref: ledgerRef, children: _jsx("div", { className: styles.thread, ref: threadRef, children: entries.length === 0 && step === "source" ? (_jsxs("div", { className: styles.hero, children: [_jsx("h1", { className: styles.heroTitle, children: ru
                                    ? "Давайте напишем текст песни! О чём она будет?"
                                    : "Let's write your song! What is it about?" }), _jsx("p", { className: styles.heroSub, children: ru
                                    ? "Сначала придумаем текст — это бесплатно. Музыка — следующим шагом."
                                    : "First the lyrics — it's free. Music is the next step." }), _jsx(motion.div, { className: cn(styles.cardsGrid, styles.cardsGrid3), variants: staggerContainer, initial: "hidden", animate: "show", children: reasons.map((r) => (_jsx(motion.button, { className: styles.optionCard, onClick: () => pickReason(r.title), variants: chipItem, children: _jsx("span", { className: styles.optionLabel, children: r.title }) }, r.id))) }), _jsxs("div", { className: styles.secondaryRow, style: { justifyContent: "center" }, children: [_jsx("button", { className: styles.secondaryPill, onClick: openCustom, children: ru ? "Вставить свой текст" : "Paste your text" }), _jsx("button", { className: styles.secondaryPill, onClick: openSaved, children: ru ? "Мои сохранённые" : "My saved" })] }), _jsx("div", { className: styles.heroHint, children: ru ? "или опишите своими словами ниже" : "or describe it below" })] })) : (_jsxs(_Fragment, { children: [entries.map((e) => {
                                const isNew = animatedIdsRef.current.has(e.id);
                                return e.role === "user" ? (_jsx(motion.div, { className: cn(styles.row, styles.rowUser), variants: userRowVariants, initial: isNew ? "hidden" : false, animate: "show", children: _jsx("div", { className: styles.userBubble, children: e.text }) }, e.id)) : (_jsxs(motion.div, { className: styles.row, variants: assistantRowVariants, initial: isNew ? "hidden" : false, animate: "show", children: [_jsx("img", { className: styles.avatar, src: AVATAR_SRC, alt: "" }), _jsx("div", { className: styles.assistantBubble, children: e.result ? (_jsxs(_Fragment, { children: [e.result.title && (_jsx("div", { className: styles.assistantTitle, children: e.result.title })), _jsx("div", { className: styles.lyrics, children: e.result.lyrics }), e.body && (_jsx("div", { className: styles.lyricsFollowUp, children: _jsx(TypedText, { text: e.body, enabled: isNew, onTick: scrollToBottom }) }))] })) : (_jsxs(_Fragment, { children: [e.title && _jsx("div", { className: styles.assistantTitle, children: e.title }), e.body && (_jsx("div", { className: styles.assistantBody, children: _jsx(TypedText, { text: e.body, enabled: isNew, onTick: scrollToBottom }) })), e.list && (_jsx("ul", { className: styles.bodyList, children: e.list.map((li, idx) => (_jsx("li", { children: li }, idx))) }))] })) })] }, e.id));
                            }), renderControl()] })) }) }), _jsxs("div", { className: styles.inputBar, children: [recorder.state === "recording" && (_jsxs("div", { className: styles.voicePanel, role: "status", children: [_jsx("span", { className: styles.voiceTimer, "aria-hidden": "true", children: formatVoiceTimer(recorder.durationMs) }), _jsx("div", { className: styles.voiceMeter, children: _jsx(MicLevelMeter, { stream: recorder.stream }) }), _jsx("span", { className: styles.voiceText, children: ru
                                    ? "Идёт запись, до 1 минуты. Нажмите на микрофон, чтобы остановить."
                                    : "Recording, up to 1 minute. Tap the microphone to stop." })] })), recorder.state !== "recording" && voiceHint && (_jsxs("div", { className: styles.voicePanel, role: "status", children: [isTranscribing && _jsx(Loader, { size: "sm" }), _jsx("span", { className: styles.voiceText, children: voiceHint })] })), _jsxs("div", { className: styles.inputInner, children: [_jsx("textarea", { className: styles.input, rows: 1, value: input, disabled: !cfg.enabled || busy, placeholder: cfg.placeholder, onChange: (e) => setInput(e.target.value), onKeyDown: (e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        onSend();
                                    }
                                } }), voiceStrategy !== "none" && (_jsx("button", { type: "button", className: cn(styles.micBtn, isMicRecording && styles.micBtnActive), onClick: onMicClick, 
                                // Пока идёт запись — кнопка активна всегда: ею же её и останавливают.
                                disabled: !isMicRecording && (!cfg.enabled || busy || isTranscribing), "aria-pressed": isMicRecording, "aria-label": isMicRecording
                                    ? ru
                                        ? "Остановить запись"
                                        : "Stop recording"
                                    : isTranscribing
                                        ? ru
                                            ? "Расшифровываю запись"
                                            : "Transcribing the recording"
                                        : ru
                                            ? "Голосовой ввод"
                                            : "Voice input", children: isTranscribing ? _jsx(Loader, { size: "sm" }) : _jsx(IconMicrophone, { size: 20 }) })), _jsx("button", { className: styles.sendBtn, onClick: onSend, disabled: !cfg.enabled || busy || (step !== "savedPick" && !input.trim()), "aria-label": ru ? "Отправить" : "Send", children: busy ? _jsx(Loader, { size: "sm" }) : "↑" })] })] })] }));
};
